import React from 'react';
import { useNavigate } from 'react-router-dom';

interface NavBarProps {
  userName: string;
  onSignOut: () => void;
  inviteCount?: number;
  onInvitesClick?: () => void;
  onInviteUserClick?: () => void;
  onShowInvitesClick?: () => void;
  hideProfileButton?: boolean;
  hideInviteButton?: boolean;
  leftSlot?: React.ReactNode;
}

const NavBar: React.FC<NavBarProps> = ({ userName, onSignOut, inviteCount = 0, onInvitesClick, onInviteUserClick, onShowInvitesClick, hideProfileButton, hideInviteButton, leftSlot }) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = React.useState(false);
  const profileRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    }
    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  return (
    <div className="fixed w-full px-4 top-[46px] z-10">
      <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
        <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
        <div className="relative h-full flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            {leftSlot}
            <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
            <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
          </div>
          <div className="flex items-center gap-3">
            {/* New Board Actions Dropdown */}
            {!hideInviteButton && (
              <div className="relative">
                <button
                  className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors relative"
                  onClick={() => setDropdownOpen(v => !v)}
                  title="Board Actions"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8M12 8v8" />
                  </svg>
                  {inviteCount > 0 && (
                    <div
                      className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center border-2 border-white shadow"
                      style={{ fontSize: '0.75rem', lineHeight: 1 }}
                      title="Pending Invitations"
                    >
                      {inviteCount}
                    </div>
                  )}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border rounded shadow-lg z-50">
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                      onClick={() => { setDropdownOpen(false); onShowInvitesClick && onShowInvitesClick(); }}
                    >
                      Pending Board Invitations {inviteCount > 0 && <span className="ml-2 bg-red-500 text-white rounded-full px-2 text-xs">{inviteCount}</span>}
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                      onClick={() => { setDropdownOpen(false); onInviteUserClick && onInviteUserClick(); }}
                    >
                      Invite User to Board
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Profile Button with Dropdown */}
            {!hideProfileButton && (
              <div className="relative" ref={profileRef}>
                <button
                  className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
                  onClick={() => setProfileDropdownOpen(v => !v)}
                  title="Profile"
                >
                  <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
                </button>
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                      onClick={() => { setProfileDropdownOpen(false); navigate('/profile'); }}
                    >
                      Profile
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                      onClick={() => { setProfileDropdownOpen(false); onSignOut(); }}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavBar; 